import { useQuery } from '@tanstack/react-query';
import {
  ETAPAS,
  cadeirasTomadas,
  faixaDaCamada,
  faixaPorExtenso,
  formatarNumeroDaCadeira,
  type CadeiraTomada,
  type CamadaId,
} from '@aponte/shared';
import { api } from '../../api/cliente.js';

export interface SeletorDeCadeiraProps {
  camada: CamadaId;
  valor: string;
  aoMudar: (numero: string) => void;
  /** Ficha sendo editada — a cadeira dela nunca aparece como tomada por outro. */
  candidatoId?: string;
  somenteLeitura?: boolean;
  erro?: string;
  /** Substitui a faixa na linha de dica — usada quando a cadeira já está congelada. */
  dica?: string;
}

/**
 * Escolha da cadeira, com as já tomadas desabilitadas.
 *
 * É lista e não campo de texto porque a numeração é fechada: 001 a 100, cada camada com
 * sua faixa (ver `FAIXAS`). Digitar só produzia duas idas ao servidor para descobrir o que
 * o cockpit já sabe — a faixa da camada e quem está com cada cadeira.
 *
 * A trava de verdade continua sendo do DynamoDB, na escrita condicional da reserva: entre
 * carregar esta lista e salvar cabe outro operador. Aqui é conveniência, não garantia.
 */
export function SeletorDeCadeira({
  camada,
  valor,
  aoMudar,
  candidatoId,
  somenteLeitura,
  erro,
  dica,
}: SeletorDeCadeiraProps) {
  // Mesma chave do Painel: quem vem de lá não paga uma segunda viagem.
  const consulta = useQuery({ queryKey: ['candidatos'], queryFn: api.listarCandidatos });

  const tomadas = cadeirasTomadas(consulta.data ?? []);
  const { primeira, ultima } = faixaDaCamada(camada);

  const numeros = Array.from({ length: ultima - primeira + 1 }, (_, i) =>
    formatarNumeroDaCadeira(primeira + i),
  );

  /*
   * Número fora da faixa da camada escolhida — acontece enquanto o operador troca de
   * camada antes de renumerar. Sem esta opção o `<select>` ficaria em branco e a troca de
   * camada apagaria a cadeira em silêncio, sem ninguém pedir.
   */
  const forasteiro = valor && !numeros.includes(valor) ? valor : null;

  const livres = numeros.filter((n) => !ocupadaPorOutro(tomadas.get(n), candidatoId)).length;

  /*
   * Sem a lista, toda cadeira parece livre. Enquanto ela carrega, isso é só silêncio; se a
   * consulta falhou, é mentira — e o operador precisa saber que a conferência não
   * aconteceu, em vez de descobrir na recusa do servidor.
   */
  const rodape = consulta.isError
    ? 'Não foi possível conferir quais cadeiras já estão tomadas — o servidor recusa se o número estiver reservado.'
    : `Faixa da camada: ${faixaPorExtenso(camada)}` +
      (consulta.data ? ` · ${livres} livre${livres === 1 ? '' : 's'}` : '');

  return (
    <div className="campo">
      <label htmlFor="cadeira">Número da cadeira</label>
      <select
        id="cadeira"
        value={valor}
        disabled={somenteLeitura || consulta.isLoading}
        onChange={(e) => aoMudar(e.target.value)}
      >
        <option value="">— a definir —</option>
        {forasteiro && (
          <option value={forasteiro}>{forasteiro} · fora da faixa desta camada</option>
        )}
        {numeros.map((numero) => {
          const tomada = tomadas.get(numero);
          const bloqueada = ocupadaPorOutro(tomada, candidatoId);
          return (
            <option key={numero} value={numero} disabled={bloqueada}>
              {numero}
              {bloqueada ? ` · ${descreverOcupante(tomada!)}` : ''}
            </option>
          );
        })}
      </select>
      {erro ? (
        <div className="erro-campo">{erro}</div>
      ) : (
        <div className="dica">{dica ?? rodape}</div>
      )}
    </div>
  );
}

function ocupadaPorOutro(tomada: CadeiraTomada | undefined, candidatoId?: string): boolean {
  return !!tomada && tomada.candidatoId !== candidatoId;
}

/**
 * Quem está com a cadeira e em que ponto do funil.
 *
 * A etapa importa: cadeira presa numa ficha `recusado` ou `desistiu` continua reservada no
 * banco, e é justamente essa que o operador pode querer liberar. Sem o nome e a etapa, a
 * opção desabilitada seria um beco sem saída.
 */
function descreverOcupante(tomada: CadeiraTomada): string {
  const nome = tomada.razao.trim() || 'ficha sem nome';
  return `${nome} (${ETAPAS[tomada.etapa].rotulo.toLowerCase()})`;
}
